import { z } from 'zod';
/**
 * Corporate emblem. Stored as a small structured JSON (never a bitmap) so the
 * client can render at any size with deterministic output.
 *
 * Dimensions:
 *   - `shape`   : outer frame silhouette
 *   - `primary` : foreground / fill colour (one of the curated Charter palette)
 *   - `accent`  : secondary colour for highlights + icon stroke
 *   - `icon`    : central glyph from the curated SVG sprite set
 *
 * Later phases (research milestones) unlock additional shape / icon options;
 * the enums below grow by appending — existing emblems never become invalid.
 */
export const EMBLEM_SHAPES = [
    'hex',
    'shield',
    'roundel',
    'diamond',
    'chevron',
    'square-notched',
];
/**
 * Palette values must match the Cassette Charter tokens in tailwind.config.
 * Keeping them as string literals (not hex) means a palette retune doesn't
 * require a data migration — just restyle the CSS variable.
 */
export const EMBLEM_COLORS = [
    'brass',
    'crt-amber',
    'crt-red',
    'crt-blue',
    'crt-green',
    'ink',
    'paperDeep',
];
export const EMBLEM_ICONS = [
    'drone',
    'gear',
    'anvil',
    'rocket',
    'crucible',
    'star',
    'hex-grid',
    'lightning',
    'leaf',
    'crown',
    'compass',
    'flask',
    'wrench',
    'beacon',
    'shield-cross',
    'satellite',
    'cog-ring',
    'triangle',
    'circle-dot',
    'waves',
];
export const EmblemSchema = z.object({
    shape: z.enum(EMBLEM_SHAPES),
    primary: z.enum(EMBLEM_COLORS),
    accent: z.enum(EMBLEM_COLORS),
    icon: z.enum(EMBLEM_ICONS),
});
/**
 * Fallback emblem for any account that pre-dates the emblem system. Also used
 * as the Zod schema default when a signup payload omits the emblem.
 */
export const DEFAULT_EMBLEM = {
    shape: 'hex',
    primary: 'brass',
    accent: 'crt-amber',
    icon: 'gear',
};
/**
 * Deterministic random emblem from a seed string (e.g. userId or a UI
 * "Randomize" button counter). Used to pre-populate the EmblemDesigner with
 * three suggestions at signup without pulling a real RNG dependency.
 */
export function randomEmblem(seed) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i += 1) {
        h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    }
    const pick = (arr, offset) => {
        const n = (h + offset * 0x9e3779b9) >>> 0;
        return arr[n % arr.length];
    };
    let primary = pick(EMBLEM_COLORS, 1);
    let accent = pick(EMBLEM_COLORS, 2);
    // Avoid primary == accent on the random path — looks flat.
    if (accent === primary) {
        accent = EMBLEM_COLORS[(EMBLEM_COLORS.indexOf(primary) + 1) % EMBLEM_COLORS.length];
    }
    return {
        shape: pick(EMBLEM_SHAPES, 0),
        primary,
        accent,
        icon: pick(EMBLEM_ICONS, 3),
    };
}
//# sourceMappingURL=emblem.js.map